import { i18n } from "@/i18n";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import moment from "moment-timezone";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { meetingAPI } from "@/api";
import OutcomeStatusV2 from "@/constants/OutcomeStatusV2";
import OutcomeSortType from "@/constants/OutcomeSortType";
import OutcomeType from "@/constants/OutcomeType";
import MyCommitmentPermissions from "@/permissions/MyCommitmentPermissions";
import { initWebSocketListeners, loadMyOutstandingOutcomes } from "./myCommitmentsApiOrchestrator";

initWebSocketListeners();

const DEFAULT_PAGE_SIZE = 25;

const _state = {
	meta: {
		commitments: {},
		currentOffsetForCompleted: 0,
		hasInitiallyLoadedCompleted: false,
		hasInitiallyLoadedOutstanding: false,
		hasLoadedCompleted: false,
		hasLoadedOutstanding: false,
		isCancelingLoad: false,
		isChangingCompletionStatus: false,
		isLoadingCompleted: false,
		isLoadingOutstanding: false,
		personId: null,
	},
};

const _getters = {
	commitmentMeta: state => {
		return state.meta;
	},
	completedCommitments: state => {
		return orderBy(
			Object.values( state.meta.commitments ).filter( x => !!x.completed ),
			[ "completed", "content", ],
			[ "desc", "asc", ]
		);
	},
	hasCommitmentId: state => id => {
		return state.meta.commitments[ id ];
	},
	hasCommitments: state => {
		return !isEmpty( state.meta.commitments );
	},
	hasInitiallyLoadedCompleted: state => {
		return !!state.meta.hasInitiallyLoadedCompleted;
	},
	hasInitiallyLoadedOutstanding: state => {
		return !!state.meta.hasInitiallyLoadedOutstanding;
	},
	hasLoadedCompleted: state => {
		return !!state.meta.hasLoadedCompleted;
	},
	isChangingCompletionStatus: state => {
		return !!state.meta.isChangingCompletionStatus;
	},
	isLoadingCompleted: state => {
		return !!state.meta.isLoadingCompleted;
	},
	isLoadingOutstanding: state => {
		return !!state.meta.isLoadingOutstanding;
	},
	outstandingCommitments: state => {
		return orderBy(
			Object.values( state.meta.commitments ).filter( x => !x.completed ),
			[ "dueDate", "created", "content", ]
		);
	},
};

function calculateNewCommitmentsState( { commitments, rootGetters, } ) {
	const timezone = rootGetters[ "auth/timezone" ];
	const userProfile = rootGetters[ "userProfile/userProfile" ];
	return commitments.reduce( ( accum, c ) => {
		const perms = new MyCommitmentPermissions( {
			outcome: c,
			userProfile,
			timezone,
		} );
		c.permissions = perms.resolve();
		accum[ c.id ] = c;
		return accum;
	}, {} );
}

const actions = {
	cancelMyCommitmentLoading: ( { getters, } ) => {
		const meta = getters.commitmentMeta;
		meta.outstandingRequest.cancel();
	},
	changeMyCommitmentCompletion: async ( { commit, getters, rootGetters, }, { commitmentId, isCompleted, } ) => {
		let meta = cloneDeep( getters.commitmentMeta );
		meta.isChangingCompletionStatus = true;
		commit( "commitmentsMetaUpdated", { meta, } );
		Nprogress.start();
		try {
			const commitment = meta.commitments[ commitmentId ];
			const {
				meetingId,
				topicId,
				id,
				type,
				content,
				dueDate: due,
				outcomeVersion,
				assignments,
			} = commitment;
			let updatedCommitment;
			if ( outcomeVersion === "v2" ) {
				updatedCommitment = await meetingAPI.saveOneOnOneCommitment( {
					commitment: {
						id,
						type,
						completed: isCompleted ? moment().format() : null,
						content,
						due,
						assignees: assignments.map( a => a.assignedId ),
						status: OutcomeStatusV2.COMPLETE,
					},
				} );
			} else {
				updatedCommitment = await meetingAPI.saveOutcomeCompletion( {
					meetingId,
					topicId,
					outcomeId: commitmentId,
					isCompleted,
				}, { version: "2", } );
			}
			// we just performed an async operation and underlying state could
			// have changed so we grab our state again to ensure we're current
			meta = cloneDeep( getters.commitmentMeta );
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments: [ updatedCommitment, ], rootGetters, } ),
			};
			meta.isChangingCompletionStatus = false;
			commit( "commitmentsMetaUpdated", { meta, } );
		} catch ( e ) {
			const message = i18n.t( "commitmentsStore.store_updateCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		Nprogress.done();
	},
	initializeMyCommitments: async ( { commit, getters, }, { personId, } ) => {
		let meta = cloneDeep( getters.commitmentMeta );
		const outstandingRequest = loadMyOutstandingOutcomes( { personId, outcomeStatus: OutcomeStatusV2.OUTSTANDING, } );
		if ( meta ) {
			meta.currentOffsetForCompleted = 0;
			meta.isChangingCompletionStatus = false;
			meta.isLoadingCompleted = false;
			meta.isLoadingOutstanding = true;
			meta.outstandingRequest = outstandingRequest;
			meta.personId = personId;
		} else {
			meta = {
				commitments: {},
				currentOffsetForCompleted: 0,
				hasInitiallyLoadedCompleted: false,
				hasInitiallyLoadedOutstanding: false,
				hasLoadedCompleted: false,
				hasLoadedOutstanding: false,
				isChangingCompletionStatus: false,
				isLoadingCompleted: false,
				isLoadingOutstanding: true,
				outstandingRequest,
				personId,
			};
		}
		commit( "commitmentsMetaUpdated", { meta, } );
		outstandingRequest.start();
	},
	loadMyOutstandingCommitments: async ( { commit, getters, rootGetters, }, { commitments, isDone, } ) => {
		const meta = cloneDeep( getters.commitmentMeta );
		try {
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments, rootGetters, } ),
			};
			meta.isLoadingOutstanding = !isDone;
			meta.hasInitiallyLoadedOutstanding = true;
			commit( "commitmentsMetaUpdated", { meta, } );
		} catch ( e ) {
			meta.isLoadingOutstanding = false;
			commit( "commitmentsMetaUpdated", { meta, } );
			const message = i18n.t( "commitmentsStore.store_loadCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
	},
	loadPageOfMyCompletedCommitments: async ( { commit, getters, rootGetters, }, { personId, } ) => {
		let meta = cloneDeep( getters.commitmentMeta );
		meta.isLoadingCompleted = true;
		commit( "commitmentsMetaUpdated", { meta, } );
		try {
			const commitments = await meetingAPI.fetchOutcomes( {
				offset: meta.currentOffsetForCompleted,
				limit: DEFAULT_PAGE_SIZE,
				personId,
				outcomeStatus: OutcomeStatusV2.COMPLETE,
				outcomeType: OutcomeType.COMMITMENT,
				sortType: OutcomeSortType.COMPLETED_RECENTLY,
			} );
			// the state could have changed due to parallel operations (loading outstanding outcomes)
			// so we grab it again now that our API call has completed
			meta = cloneDeep( getters.commitmentMeta );
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments, rootGetters, } ),
			};
			meta.isLoadingCompleted = false;
			meta.hasLoadedCompleted = commitments.length !== DEFAULT_PAGE_SIZE;
			meta.currentOffsetForCompleted += DEFAULT_PAGE_SIZE;
			meta.hasInitiallyLoadedCompleted = true;
			commit( "commitmentsMetaUpdated", { meta, } );
		} catch ( e ) {
			meta.isLoadingOutstanding = false;
			commit( "commitmentsMetaUpdated", { meta, } );
			const message = i18n.t( "commitmentsStore.store_loadCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
	},
	processUpdatedCommitment: async ( { commit, getters, rootGetters, }, { outcomeId, } ) => {
		let meta = cloneDeep( getters.commitmentMeta );
		meta.isLoadingCompleted = true;
		commit( "commitmentsMetaUpdated", { meta, } );
		try {
			const commitment = await meetingAPI.getOutcome( { outcomeId, } );
			// the state could have changed due to parallel operations (loading outstanding outcomes)
			// so we grab it again now that our API call has completed
			meta = cloneDeep( getters.commitmentMeta );
			meta.commitments = {
				...meta.commitments,
				...calculateNewCommitmentsState( { meta, commitments: [ commitment, ], rootGetters, } ),
			};
			meta.isLoadingCompleted = true;
			meta.hasLoadedCompleted = true;
			commit( "commitmentsMetaUpdated", { meta, } );
		} catch ( e ) {
			meta.isLoadingOutstanding = false;
			commit( "commitmentsMetaUpdated", { meta, } );
			const message = i18n.t( "commitmentsStore.store_loadCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
	},
	refreshMyCommitments: async ( { commit, getters, }, { outcomeId, personId, } ) => {
		if ( getters.hasCommitmentId( outcomeId ) ) {
			const meta = cloneDeep( getters.commitmentMeta );
			const outstandingRequest = loadMyOutstandingOutcomes( {
				personId,
				outcomeStatus: [ OutcomeStatusV2.OUTSTANDING, OutcomeStatusV2.COMPLETE, ],
			} );
			commit( "commitmentsMetaUpdated", { meta, } );
			outstandingRequest.start();
		}
	},
	removeMyCommitment: async ( { commit, getters, }, { outcomeId, } ) => {
		if ( getters.hasCommitmentId( outcomeId ) ) {
			const meta = cloneDeep( getters.commitmentMeta );
			delete meta.commitments[ outcomeId ];
			commit( "commitmentsMetaUpdated", { meta, } );
		}
	},
};

const mutations = {
	commitmentsMetaUpdated: ( state, { meta, } ) => {
		const currentMeta = state.meta;
		state.meta = {
			...currentMeta,
			...cloneDeep( meta ),
		};
	},
};

export default {
	state: _state,
	getters: _getters,
	mutations,
	actions,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/myCommitments/index.js